import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANG_LABELS, type UiLangCode } from '@/data/locales';

import en from './locales/en.json';
import ur from './locales/ur.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import it from './locales/it.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import sv from './locales/sv.json';
import da from './locales/da.json';
import cs from './locales/cs.json';
import hu from './locales/hu.json';
import ro from './locales/ro.json';
import bg from './locales/bg.json';
import ar from './locales/ar.json';

const resources = {
  en: { translation: en },
  ur: { translation: ur },
  ar: { translation: ar },
  de: { translation: de },
  fr: { translation: fr },
  es: { translation: es },
  it: { translation: it },
  nl: { translation: nl },
  pl: { translation: pl },
  sv: { translation: sv },
  da: { translation: da },
  cs: { translation: cs },
  hu: { translation: hu },
  ro: { translation: ro },
  bg: { translation: bg },
};

const UI_LANG_STORAGE_KEY = 'saas_pos_ui_language';

export function applyDocumentDirection(lang: string) {
  const rtl = lang === 'ur' || lang === 'ar';
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang || 'en';
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
  }
}

export function isSupportedUiLanguage(lang: string | null | undefined): lang is UiLangCode {
  return Boolean(lang && lang in LANG_LABELS);
}

function readStoredUiLanguage(): UiLangCode | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const stored = localStorage.getItem(UI_LANG_STORAGE_KEY);
    return isSupportedUiLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

const initialLang = readStoredUiLanguage() || 'en';

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLang,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

applyDocumentDirection(initialLang);

/** User override first, then company default, then English. */
export function resolveUiLanguage(userLang?: string | null, companyLang?: string | null): UiLangCode {
  if (isSupportedUiLanguage(userLang)) return userLang;
  if (isSupportedUiLanguage(companyLang)) return companyLang;
  return 'en';
}

/** Apply UI language (any supported language; not restricted by country). */
export async function setAppLanguage(lang: string | null | undefined, companyLang?: string | null) {
  const next = resolveUiLanguage(lang, companyLang);
  applyDocumentDirection(next);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(UI_LANG_STORAGE_KEY, next);
    }
  } catch {
    /* private browsing */
  }
  if (i18n.language !== next) {
    await i18n.changeLanguage(next);
  }
  return next;
}

export default i18n;
